import { ContractState, ContractViewModel } from '@ail/contracts-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useGetContractQuery, useGetContractsBySecurityKeysQuery } from '../../../redux/api/contractsApi'
import { productTypeIdDict } from '../../../types/constants'
import { Contracts } from '../../../utils/contracts'
import { useReplacementCondition } from '../../../hooks/useReplacementCondition'
import { useSettings } from '../../../hooks/useSettings'

export function StatusDetails(props: { contract?: ContractViewModel; onCopy: (value?: string) => void }) {
  const { t } = useTranslation('common')
  const [expiringDaysFilter] = useSettings('expiringDaysFilter')

  const customerKey = props.contract?.owner?.split('//').at(0)

  const { currentData: replacedContract } = useGetContractQuery(props.contract?.replacedContractId ?? skipToken)

  const customerContracts = useGetContractsBySecurityKeysQuery(
    customerKey ? { securityKeys: [customerKey] } : skipToken
  )

  const futureContract = useMemo(() => {
    if (!props.contract || !customerContracts.currentData) {
      return undefined
    }
    return customerContracts.currentData.find((c) => c.replacedContractId === props.contract?.id)
  }, [customerContracts.currentData, props.contract])

  const contractState = useMemo(() => {
    if (!props.contract) {
      return undefined
    }
    return Contracts.getExtendedContractState(props.contract, Number(expiringDaysFilter))
  }, [props.contract, expiringDaysFilter])

  const { getReplacementCondition } = useReplacementCondition()

  const {
    missing: missingReplacementOrder,
    processing: processingReplacementOrder,
    withoutReplacement,
  } = useMemo(() => {
    const state = { missing: false, processing: false, withoutReplacement: false }

    if (props.contract) {
      const endDate = new Date(props.contract.endDate)
      const { missingReplacementOrder, processingOrder, returnWithoutReplacement } = getReplacementCondition({
        endIntention: props.contract.endIntention,
        endDate: endDate.getTime() < 32535122400000 ? endDate : undefined,
        expiringContractsFilter: expiringDaysFilter,
        futureContract,
        gallikerReference: props.contract.vehicle?.GallikerReference,
        state: contractState,
      })
      state.missing = missingReplacementOrder
      state.processing = processingOrder
      state.withoutReplacement = returnWithoutReplacement
    }

    return state
  }, [contractState, futureContract, getReplacementCondition, props.contract])

  return (
    <div className="detailContentArea">
      <div className="detailTitle withStatus">
        <div className="titleIcon">
          <div>
            <i className="icon-vat" /> <span>{t('CONTRACT_DETAILS')}</span>
          </div>
        </div>
        <div className="statusIcon">
          {!!replacedContract?.reference && <i className="icon-change-car" />}
          <div className={`statusArea ${contractState?.toLowerCase()}`}>
            {contractState && t(`CONTRACT_${contractState.toUpperCase()}`)}
          </div>
        </div>
      </div>
      <div className="details contDetails">
        <div className="row">
          <div
            className="col-6"
            style={props.contract?.reference ? { cursor: 'pointer' } : undefined}
            onClick={() => props.onCopy(props.contract?.reference)}
          >
            <label style={props.contract?.reference ? { cursor: 'pointer' } : undefined}>{t('REFERENCE')}</label>
            {props.contract?.reference}
          </div>
          <div className="col-6">
            <label>{t('PRODUCT')}</label>
            {props.contract?.productId
              ? t(
                  `PRODUCT_SHORT_${
                    productTypeIdDict[props.contract.productId.toString() as keyof typeof productTypeIdDict]
                  }`.toUpperCase()
                )
              : '-'}
          </div>
          {props.contract?.vehicle?.GallikerReference && (
            <div
              className="col-6"
              style={props.contract.vehicle.GallikerReference ? { cursor: 'pointer' } : undefined}
              onClick={() => props.onCopy(props.contract?.vehicle?.GallikerReference)}
            >
              <label style={props.contract.vehicle.GallikerReference ? { cursor: 'pointer' } : undefined}>
                {t('LOGISTICID')}
              </label>
              {props.contract.vehicle.GallikerReference}
            </div>
          )}
        </div>
        {!!replacedContract?.reference && (
          <div className="row replacedCon future">
            <div className="col-6">
              <label>{t('REPLACED_CONTRACT')}</label>
              <NavLink to={`/generalContracts/details/${replacedContract.id}`}>{replacedContract?.reference}</NavLink>
            </div>
          </div>
        )}
        {!!futureContract && (
          <div className="row replacedCon" style={{ marginTop: '10px' }}>
            <div className="col-6">
              <label>{t('WILL_BE_REPLACED_BY')}</label>
              <div className="flex">
                <i className="icon-change-car" />
                <NavLink
                  exact
                  to={`/${
                    futureContract.state === ContractState.Initialized ? 'orderAndDelivery' : 'generalContracts'
                  }/details/${futureContract.id}`}
                >
                  {futureContract.reference}
                </NavLink>
              </div>
            </div>
            {!!futureContract.vehicle?.GallikerReference && (
              <div className="col-6">
                <label>{t('LOGISTICID')}</label>
                {futureContract.vehicle.GallikerReference}
              </div>
            )}
          </div>
        )}
        {missingReplacementOrder && !processingReplacementOrder && !withoutReplacement && (
          <div className="row replacedCon missingReplacement" style={{ marginTop: '10px' }}>
            <i className="icon-change-car" />
            <label>{t('MISSING_REPLACEMENT_ORDER')}</label>
            <i className="icon-warning" />
          </div>
        )}
        {!missingReplacementOrder && processingReplacementOrder && !withoutReplacement && (
          <div className="row replacedCon processingReplacement" style={{ marginTop: '10px' }}>
            <i className="icon-change-car" />
            <label>
              {t('STATUS_PENDING')} / {t('PROCESSING')}
            </label>
            <i className="icon-pending" />
          </div>
        )}
        {!missingReplacementOrder && !processingReplacementOrder && withoutReplacement && (
          <div className="row replacedCon processingReplacement" style={{ marginTop: '10px' }}>
            <i className="icon-change-car" />
            <label>{t('RETURN_WITHOUT_REPLACEMENT')}</label>
            <i className="icon-delivery" />
          </div>
        )}
      </div>
    </div>
  )
}
